import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, Pie } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import API from '../api';

// Register Chart.js components
Chart.register(...registerables);

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [extractionHistory, setExtractionHistory] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [lineChartData, setLineChartData] = useState(null);
  const [pieChartData, setPieChartData] = useState(null);
  const [globalPostalCodeStats, setGlobalPostalCodeStats] = useState([]);
  const [freshDepartmentStats, setFreshDepartmentStats] = useState([]);
  const [nafCodesMap, setNafCodesMap] = useState({});

  // Vérifier si l'utilisateur a déjà vu les instructions
  useEffect(() => {
    const checkUserInstructions = async () => {
      try {
        const userInfoResponse = await API.get('/api/auth/user-info');
        const hasViewedInstructions = userInfoResponse.data.hasViewedInstructions;
        
        // Si l'utilisateur n'a pas encore vu les instructions, le rediriger
        if (!hasViewedInstructions) {
          navigate('/instructions');
        }
      } catch (err) {
        console.error("Erreur lors de la vérification des instructions:", err);
        // En cas d'erreur, continuer à charger le dashboard normalement
      }
    };

    checkUserInstructions();
  }, [navigate]);

  // Fetch extraction history data and NAF codes
  useEffect(() => {
    const fetchExtractionHistory = async () => {
      try {
        setLoading(true);
        // Récupérer l'historique et les codes NAF
        const [freshResponse, globalResponse, nafCodesResponse] = await Promise.all([
          API.get('/api/extraction/history/fresh'),
          API.get('/api/extraction/history/global'),
          fetch('/codes_naf.json').then(res => res.json())
        ]);

        // Process NAF codes into a lookup map
        const nafLookup = {};
        nafCodesResponse.filter(item => item["Code NAF"]).forEach(item => {
          nafLookup[item["Code NAF"]] = item.Description;
        });
        setNafCodesMap(nafLookup);

        // Combine the results
        const combinedHistory = [...freshResponse.data, ...globalResponse.data];
        
        // Sort by timestamp
        combinedHistory.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        );

        setExtractionHistory(combinedHistory);
        processExtractionData(combinedHistory, nafLookup);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching extraction history:", err);
        setError("Impossible de charger les données d'extraction. Veuillez réessayer plus tard.");
        setLoading(false);
      }
    };

    fetchExtractionHistory();
  }, []);

  // Process the extraction data for various visualizations
  const processExtractionData = (data, nafLookup) => {
    if (!data || data.length === 0) return;

    // Calculate total leads
    const total = data.reduce((sum, item) => sum + (item.count || 0), 0);
    setTotalLeads(total);

    // Process data for line chart (dates and lead counts)
    processLineChartData(data);

    // Process data for pie chart (NAF code categories) - Improved accuracy
    processPieChartData(data, nafLookup);

    // Process data for GLOBAL postal code table
    const globalData = data.filter(item => item.extraction_type === "G.L.O.B.A.L");
    processGlobalPostalCodeData(globalData, total);
    
    // Process data for FRESH department table
    const freshData = data.filter(item => item.extraction_type === "F.R.E.S.H");
    processFreshDepartmentData(freshData, total);
  };

  // Process data for the line chart
  const processLineChartData = (data) => {
    // Group data by date and sum counts
    const dateGroups = data.reduce((groups, item) => {
      const date = new Date(item.timestamp).toLocaleDateString('fr-FR');
      if (!groups[date]) groups[date] = 0;
      groups[date] += (item.count || 0);
      return groups;
    }, {});

    // Convert to arrays for Chart.js
    const dates = Object.keys(dateGroups);
    const counts = Object.values(dateGroups);

    setLineChartData({
      labels: dates,
      datasets: [
        {
          label: 'Leads générés',
          data: counts,
          borderColor: '#FF8C00',
          backgroundColor: 'rgba(255, 140, 0, 0.2)',
          tension: 0.4,
          fill: true,
        }
      ]
    });
  };

  // Improved accuracy for NAF code distribution
  const processPieChartData = (data, nafLookup) => {
    const nafCodeTotals = {};
    
    // Process Global extraction data - Each lead is tied to specific NAF code(s)
    const globalData = data.filter(item => 
      item.extraction_type === "G.L.O.B.A.L" && 
      item.codes_naf && 
      Array.isArray(item.codes_naf)
    );
    
    // For global data, count leads per NAF code based on extraction parameters
    globalData.forEach(item => {
      if (item.count && item.codes_naf.length > 0) {
        // Distribute count proportionally among the NAF codes
        const perCode = Math.round(item.count / item.codes_naf.length);
        item.codes_naf.forEach(code => {
          if (!nafCodeTotals[code]) nafCodeTotals[code] = 0;
          nafCodeTotals[code] += perCode;
        });
      }
    });
    
    // Process Fresh extraction data - each entry might have NAF data in a different format
    const freshData = data.filter(item => item.extraction_type === "F.R.E.S.H");
    
    freshData.forEach(item => {
      // For Fresh data, we'll use any code_naf field if available
      if (item.code_naf) {
        if (!nafCodeTotals[item.code_naf]) nafCodeTotals[item.code_naf] = 0;
        nafCodeTotals[item.code_naf] += (item.count || 0);
      }
    });
    
    console.log(`Found ${Object.keys(nafCodeTotals).length} unique NAF codes in history`);
    
    if (Object.keys(nafCodeTotals).length === 0) {
      console.log("No NAF data found in extraction history");
      setPieChartData({
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 1,
        }]
      });
      return;
    }

    const topCategories = Object.entries(nafCodeTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    
    const categoryLabels = topCategories.map(([code, _]) => {
      const description = nafLookup[code] || `Code NAF ${code}`;
      return description.length > 25 ? 
        `${code} - ${description.substring(0, 25)}...` : 
        `${code} - ${description}`;
    });
    
    const categoryValues = topCategories.map(([_, count]) => count);

    const backgroundColors = ['#FF8C00', '#4CAF50', '#2196F3', '#9C27B0', '#F44336'];
    const borderColors = ['#E67E00', '#3B8C3B', '#1A75C1', '#7B1FA2', '#D32F2F'];

    setPieChartData({
      labels: categoryLabels,
      datasets: [
        {
          data: categoryValues,
          backgroundColor: backgroundColors.slice(0, categoryValues.length),
          borderColor: borderColors.slice(0, categoryValues.length),
          borderWidth: 1,
        }
      ]
    });
  };

  // Process data for GLOBAL postal code table
  const processGlobalPostalCodeData = (data, totalCount) => {
    const postalCodes = {};
    data.forEach(item => {
      if (item.deps_or_codes && Array.isArray(item.deps_or_codes)) {
        item.deps_or_codes.forEach(code => {
          if (!postalCodes[code]) postalCodes[code] = 0;
          postalCodes[code] += (item.count || 0) / (item.deps_or_codes.length || 1);
        });
      }
    });

    const globalTotal = data.reduce((sum, item) => sum + (item.count || 0), 0);

    const postalCodeStats = Object.entries(postalCodes)
      .map(([code, count]) => ({
        code,
        count: Math.round(count),
        percentage: (count / globalTotal * 100).toFixed(1)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Limit to top 10 for display

    setGlobalPostalCodeStats(postalCodeStats);
  };

  // Process data for FRESH department table
  const processFreshDepartmentData = (data, totalCount) => {
    // Group by department and calculate total leads
    const departmentStats = [];
    const departmentMap = {};
    
    data.forEach(item => {
      if (item.deps_or_codes && Array.isArray(item.deps_or_codes)) {
        const timestamp = new Date(item.timestamp);
        const dateStr = timestamp.toLocaleDateString('fr-FR');
        
        item.deps_or_codes.forEach(dep => {
          const key = dep;
          if (!departmentMap[key]) {
            departmentMap[key] = {
              department: dep,
              count: 0,
              dates: new Set()
            };
          }
          departmentMap[key].count += (item.count || 0);
          departmentMap[key].dates.add(dateStr);
        });
      }
    });
    
    const freshTotal = data.reduce((sum, item) => sum + (item.count || 0), 0);
    
    // Convert map to array and sort by lead count
    Object.values(departmentMap).forEach(entry => {
      const dateArray = Array.from(entry.dates).sort();
      let dateRange = "N/A";
      
      if (dateArray.length === 1) {
        dateRange = dateArray[0];
      } else if (dateArray.length > 1) {
        dateRange = `${dateArray[0]} - ${dateArray[dateArray.length - 1]}`;
      }
      
      departmentStats.push({
        department: entry.department,
        dateRange,
        count: Math.round(entry.count),
        percentage: (entry.count / freshTotal * 100).toFixed(1)
      });
    });
    
    // Sort by count descending
    departmentStats.sort((a, b) => b.count - a.count);
    
    setFreshDepartmentStats(departmentStats.slice(0, 10)); // Top 10 departments
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Leads: ${context.raw}`;
          }
        }
      },
      legend: {
        display: true,
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Nombre de leads'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12,
          font: {
            size: 10
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.raw} leads`;
          }
        }
      }
    }
  };

  return (
    <div className="dashboard-container">
      <h2>Tableau de bord</h2>
      {loading ? (
        <p>Chargement des données...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <>
          {/* Line Chart Section - Reduced height */}
          <section className="chart-section">
            <h3>Total leads générés : {totalLeads}</h3>
            <div className="chart-container" style={{ height: '250px' }}>
              {lineChartData ? (
                <Line data={lineChartData} options={lineChartOptions} />
              ) : (
                <p>Aucune donnée disponible pour le graphique</p>
              )}
            </div>
            
            {/* Navigation Buttons */}
            <div className="nav-buttons">
              <button onClick={() => navigate('/fresh')}>Contactez les nouvelles entreprises</button>
              <button onClick={() => navigate('/global')}>Prospectez par segmentation</button>
              <button onClick={() => navigate('/assistant-ia')}>ProspX - Assistant IA</button>
            </div>
          </section>

          <div className="charts-and-table-container">
            {/* Pie Chart Section - Reduced height */}
            <section className="chart-section">
              <h3>Répartition par catégorie d'activité</h3>
              <div className="chart-container" style={{ height: '220px' }}>
                {pieChartData ? (
                  <Pie data={pieChartData} options={pieChartOptions} />
                ) : (
                  <p>Aucune donnée disponible pour le graphique</p>
                )}
              </div>
            </section>

            <div className="tables-container">
              {/* GLOBAL Postal Code Table */}
              <section className="table-section">
                <h3>Leads GLOBAL par code postal</h3>
                {globalPostalCodeStats.length > 0 ? (
                  <div className="table-wrapper" style={{ maxHeight: '220px', overflow: 'auto' }}>
                    <table className="stats-table">
                      <thead>
                        <tr>
                          <th>Code postal</th>
                          <th>Leads</th>
                          <th>Pourcentage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {globalPostalCodeStats.map((item, index) => (
                          <tr key={index}>
                            <td>{item.code}</td>
                            <td>{item.count}</td>
                            <td>{item.percentage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>Aucune donnée GLOBAL disponible</p>
                )}
              </section>

              {/* FRESH Department Table */}
              <section className="table-section">
                <h3>Leads FRESH par département</h3>
                {freshDepartmentStats.length > 0 ? (
                  <div className="table-wrapper" style={{ maxHeight: '220px', overflow: 'auto' }}>
                    <table className="stats-table">
                      <thead>
                        <tr>
                          <th>Département</th>
                          <th>Période</th>
                          <th>Leads</th>
                          <th>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {freshDepartmentStats.map((item, index) => (
                          <tr key={index}>
                            <td>{item.department}</td>
                            <td>{item.dateRange}</td>
                            <td>{item.count}</td>
                            <td>{item.percentage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>Aucune donnée FRESH disponible</p>
                )}
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;